import { useEffect, useRef } from 'react';
import { theme } from 'antd';
import { asArray } from 'ol/color';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';

// Tier 3 (custom): the tiles of an annotation session drawn on the OpenLayers
// canvas, outside the DOM antd styles. Colours come from the antd theme tokens.
// Catalogue: "Annotation overlay".

const LAYER_ID = 'annotationTileGrid';
// Between the training-data layers and the active-square outline (20).
const Z_INDEX = 19;
const FIT_PADDING = [80, 80, 80, 80];

const withAlpha = (color, alpha) => {
	const [r, g, b] = asArray(color);
	return [r, g, b, alpha];
};

/**
 * Every tile inside the session boundary, as clickable squares. With no tile
 * open, a click opens one; while a tile is open the others sit back and the
 * view stays on the open one. Saved tiles are marked.
 *
 * @param {import('ol/Map').default} props.map
 * @param {Array} props.tiles - GeoJSON tile features in the map projection
 * @param {number|null} props.activeIndex - the open tile, or null
 * @param {object} [props.savedTiles] - `{ [tileName]: … }` of tiles saved for the active object
 * @param {(index: number) => void} props.onPick
 */
const AnnotationTileGrid = ({ map, tiles, activeIndex, savedTiles = {}, onPick }) => {
	const { token } = theme.useToken();
	const layerRef = useRef(null);

	// The style function and the click handler read the latest props without
	// the layer being rebuilt on every change.
	const stateRef = useRef({ activeIndex, savedTiles, onPick, token });
	stateRef.current = { activeIndex, savedTiles, onPick, token };

	useEffect(() => {
		if (!map) return;

		const styleFor = feature => {
			const { activeIndex: open, savedTiles: saved, token: t } = stateRef.current;
			const index = feature.get('tileIndex');
			const isOpen = open === index;
			const isSaved = Boolean(saved[feature.get('tile_name')]);
			// Another tile is open: this one sits back.
			const faded = open !== null && !isOpen;

			const color = isOpen ? t.colorPrimary : isSaved ? t.colorSuccess : t.colorTextTertiary;
			return new Style({
				stroke: new Stroke({
					color: withAlpha(color, faded ? 0.25 : 1),
					width: isOpen ? t.lineWidthBold : t.lineWidth,
				}),
				fill: new Fill({
					color: withAlpha(color, faded ? 0.03 : isOpen || isSaved ? 0.12 : 0.06),
				}),
			});
		};

		const layer = new VectorLayer({
			source: new VectorSource(),
			zIndex: Z_INDEX,
			style: styleFor,
			properties: { customLayerId: LAYER_ID, name: 'Annotation tiles' },
		});
		map.addLayer(layer);
		layerRef.current = layer;

		const pick = event => {
			if (stateRef.current.activeIndex !== null) return;
			const hit = map.forEachFeatureAtPixel(event.pixel, feature => feature, {
				layerFilter: candidate => candidate === layer,
			});
			if (hit) stateRef.current.onPick?.(hit.get('tileIndex'));
		};
		map.on('singleclick', pick);

		return () => {
			map.un('singleclick', pick);
			map.removeLayer(layer);
			layerRef.current = null;
		};
	}, [map]);

	// The tiles of the session (they change when the object does).
	useEffect(() => {
		const layer = layerRef.current;
		if (!layer) return;

		const source = layer.getSource();
		source.clear();
		const features = new GeoJSON().readFeatures({ type: 'FeatureCollection', features: tiles });
		features.forEach((feature, index) => feature.set('tileIndex', index, true));
		source.addFeatures(features);

		if (features.length && stateRef.current.activeIndex === null) {
			map.getView().fit(source.getExtent(), { padding: FIT_PADDING, duration: 300 });
		}
	}, [map, tiles]);

	// Restyle on open/close and on a save; zoom back out when a tile closes.
	useEffect(() => {
		const layer = layerRef.current;
		if (!layer) return;
		layer.changed();

		if (activeIndex === null && layer.getSource().getFeatures().length) {
			map.getView().fit(layer.getSource().getExtent(), {
				padding: FIT_PADDING,
				duration: 300,
			});
		}
	}, [map, activeIndex, savedTiles]);

	return null;
};

export default AnnotationTileGrid;
